import * as React from 'react'
import DropDownChevron from '../../../assets/icons_new/DropDown.svg'
import CheckedColor from '../../../assets/icons_new/CheckedColor.svg'
import { GroupModel } from '@/app/models/groupModel'
import { FaIcon, faUsersGroupSolid, faUsersMedicalSolid } from '@/lib/fa'
import AddGroupModal from '../modals/groups/AddGroupModal'
import EditGroupModal from '../modals/groups/EditGroupModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type DropDownProps = {
  options: GroupModel[]
  companyId: string
  refreshGroups: () => void
  name?: string
  onSelect?: (option: GroupModel | null) => void
  icon?: string
  chevronIcon?: string
  actualGroup?: GroupModel | null
  fullWidth?: boolean
}

const GroupPicker: React.FC<DropDownProps> = ({
  options,
  companyId,
  refreshGroups,
  name = 'Wszyscy',
  onSelect,
  chevronIcon = DropDownChevron,
  actualGroup = null,
  fullWidth = false,
}) => {
  const { t } = useLocalize()
  const [open, setOpen] = React.useState(false)
  const [isEditGroupModelOpen, setIsEditGroupModelOpen] = React.useState(false)
  const [isAddGroupModelOpen, setIsAddGroupModelOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<GroupModel | null>(actualGroup)
  const dropdownRef = React.useRef<HTMLUListElement>(null)

  React.useEffect(() => {
    setSelected(actualGroup)
  }, [actualGroup])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const handleSelect = (option: GroupModel | null) => {
    setSelected(option)
    setOpen(false)
    onSelect?.(option)
  }

  const allOption: GroupModel = { id: 0, name: t('driverModule.all-drivers') }
  const activeOption: GroupModel = { id: -1, name: t('driverModule.active-drivers') }
  const inactiveOption: GroupModel = { id: -2, name: t('driverModule.inactive-drivers') }

  const resolveDisplayName = (group: GroupModel | null): string => {
    if (!group) return name
    if (group.id === 0) return allOption.name
    if (group.id === -1) return activeOption.name
    if (group.id === -2) return inactiveOption.name
    return group.name
  }

  React.useEffect(() => {
    if (!actualGroup && !selected) {
      setSelected(activeOption)
      onSelect?.(activeOption)
    }
  }, [])

  const fullOptions = [
    allOption,
    activeOption,
    inactiveOption,
    ...options.filter((opt) => opt.id !== 0 && opt.id > 0),
  ]

  const filteredOptions = fullOptions.filter(
    (opt) => opt.id !== (selected?.id ?? 0),
  )

  return (
    <div
      className={`tw-relative   tw-z-[100] ${fullWidth ? 'tw-w-full' : 'tw-w-fit tw-flex-shrink-0'}`}
    >
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen((prev) => !prev)
        }}
        className={`tw-z-100 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-bg-transparent tw-px-2 tw-py-1 tw-text-base-content hover:tw-bg-[#F5F5F5] ${fullWidth ? 'tw-w-full' : ''} ${
          open
            ? 'tw-border-2 tw-border-primary'
            : 'tw-border tw-border-neutral/20'
        }`}
      >
        <p className="tw-text-sm">{resolveDisplayName(selected)}</p>
        <img
          src={chevronIcon}
          alt="dropdown icon"
          className={`tw-ml-2 tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 ${
            open ? 'tw-rotate-180' : ''
          }`}
        />
      </button>

      <ul
        ref={dropdownRef}
        className={`tw-absolute tw-left-0 tw-z-50 tw-mt-2 tw-w-fit ${fullWidth ? 'tw-w-full' : 'tw-max-w-[400px]'} tw-overflow-hidden tw-rounded tw-bg-white tw-p-1.5 tw-shadow-md tw-transition-all tw-duration-200 ${
          open
            ? 'tw-scrollbar-thin tw-max-h-[500px] tw-overflow-y-auto tw-opacity-100'
            : 'tw-max-h-0 tw-opacity-0'
        }`}
      >
        {selected ? (
          <li
            key={selected.id}
            className="tw-font-sm tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-text-ellipsis tw-rounded tw-bg-[#E9EDF9] tw-px-1 tw-py-2"
            onClick={() => handleSelect(selected)}
          >
            <p>{resolveDisplayName(selected)}</p>
            <img src={CheckedColor} alt="Checked Icon" />
          </li>
        ) : (
          <li
            key={allOption.id}
            className="tw-font-sm tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-text-ellipsis tw-rounded tw-bg-[#E9EDF9] tw-px-1 tw-py-2"
            onClick={() => handleSelect(allOption)}
          >
            <p>{allOption.name}</p>
            <img src={CheckedColor} alt="Checked Icon" />
          </li>
        )}

        <li className="tw-mb-1.5 tw-mt-2.5 tw-border-t tw-border-gray-200"></li>

        {filteredOptions.map((option) => (
          <li
            key={option.id}
            className="tw-font-sm tw-cursor-pointer tw-text-ellipsis tw-rounded tw-px-1 tw-py-2 hover:tw-bg-gray-100"
            onClick={() => handleSelect(option.id === 0 ? null : option)}
          >
            {option.name}
          </li>
        ))}

        <div className="tw-sticky tw-bottom-0 tw-bg-inherit">
          <li className="tw-my-1 tw-border-t tw-border-gray-200"></li>
          <li
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded tw-px-1 tw-py-2 tw-text-gray-500 hover:tw-bg-gray-100"
            onClick={() => {
              setIsAddGroupModelOpen(true)
              setOpen(false)
            }}
          >
            <FaIcon icon={faUsersMedicalSolid} className="tw-text-primary" />
            <p className="tw-text-nowrap tw-text-sm tw-font-medium tw-text-primary">
              {t('driverModule.add-new-group')}
            </p>
          </li>
          <li
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded tw-px-1 tw-py-2 tw-text-gray-500 hover:tw-bg-gray-100"
            onClick={() => {
              setIsEditGroupModelOpen(true)
              setOpen(false)
            }}
          >
            <FaIcon icon={faUsersGroupSolid} className="tw-text-primary" />
            <p className="tw-text-nowrap tw-text-sm tw-font-medium tw-text-primary">
              {t('driverModule.edit-groups')}
            </p>
          </li>
        </div>
      </ul>
      {isEditGroupModelOpen && options.length > 0 && (
        <EditGroupModal
          companyId={companyId}
          groups={options}
          close={() => setIsEditGroupModelOpen(false)}
        />
      )}
      {isAddGroupModelOpen && (
        <AddGroupModal
          openGroupMembers={true}
          companyId={companyId}
          refreshGroups={refreshGroups}
          close={() => setIsAddGroupModelOpen(false)}
        />
      )}
    </div>
  )
}

export default GroupPicker
